import { useEffect, useRef, useState, type ReactElement } from 'react';
import { PLACEMENT_GAMES, display, isRanked, pilotEloSeries, rankFor, rankThrough } from '@edh/shared';
import { MemberProfile } from './components/MemberProfile';
import { RankedAvatar } from './components/RankedAvatar';
import { Register } from './components/Register';
import { SignIn } from './components/SignIn';
import { BarsIcon, CrownIcon, DeckIcon, SwordsIcon } from './components/TabIcons';
import { registerSelf } from './lib/api';
import { stored, subject } from './lib/cognito';
import { navigate, useRoute, type Route } from './lib/router';
import { PullToRefresh } from './components/PullToRefresh';
import { StoreProvider, useStore } from './lib/store';
import { warmArtCache } from './lib/art';
import { DeckDetail } from './screens/DeckDetail';
import { Decks } from './screens/Decks';
import { Play } from './screens/Play';
import { Guild } from './screens/Guild';
import { Standings } from './screens/Standings';

// Ordered by what you reach for at the table: log the game you just played,
// then the decks for the next one. Standings are for afterwards.
const TABS: { route: Route['name']; label: string; Icon: () => ReactElement }[] = [
  { route: 'log', label: 'Play', Icon: SwordsIcon },
  { route: 'decks', label: 'Decks', Icon: DeckIcon },
  { route: 'guildmates', label: 'Guild', Icon: CrownIcon },
  { route: 'standings', label: 'Standings', Icon: BarsIcon },
];

export function App() {
  const [signedIn, setSignedIn] = useState(() => stored() !== null);
  if (!signedIn) return <SignIn onSignedIn={() => setSignedIn(true)} />;
  return (
    <StoreProvider>
      <Gate />
    </StoreProvider>
  );
}

/**
 * Signed in, but not necessarily a member yet. The first thing an invited
 * account does is say who it is on the standings.
 */
function Gate() {
  const { state, loading, hydrated, sync } = useStore();
  const [registered, setRegistered] = useState(false);
  const backfilled = useRef(false);
  const sub = subject();

  const me = sub ? state.pilots.find((p) => p.sub === sub) : undefined;

  /**
   * Wait for the server, not just the cache.
   *
   * "No member record" is only meaningful once the league has actually
   * arrived. Deciding it from a cold cache flashes the registration form at
   * somebody who has been a member for weeks, and if they are quick enough to
   * act on it, hands them a second identity.
   */
  if (loading || !hydrated) {
    return (
      <div className="signin" style={{ alignItems: 'center' }}>
        <span className="spinner" />
      </div>
    );
  }

  // Members who joined before the email field existed have a blank row. The
  // register endpoint fills it from Cognito and returns the existing record,
  // so this is a no-op for everyone else. Attempted once per session.
  if (me && !me.email && !backfilled.current) {
    backfilled.current = true;
    void registerSelf({ name: me.name }).then(() => sync());
  }

  if (sub && !me && !registered) {
    return (
      <Register
        unclaimed={state.pilots.filter((p) => !p.sub && !p.retired)}
        onDone={() => {
          setRegistered(true);
          void sync();
        }}
      />
    );
  }

  return <Shell />;
}

/**
 * You, in the corner of every screen.
 *
 * The rank is the ring, as it is everywhere else in the app — the same drawing
 * on the same face, so nothing new has to be learned to read it. Beside it the
 * rating, which is the one number a league is actually about, and during
 * placement the count of games instead: five is what it takes before there is
 * a rating worth printing, and printing one anyway is a claim the app cannot
 * yet support.
 *
 * Nothing else. It sits above every screen, and a header that grows is a header
 * that starts competing with what it is a header for.
 */
function YouChip() {
  const { state, me } = useStore();
  const [open, setOpen] = useState(false);
  if (!me) return null;

  const ranked = isRanked(me.placementGames);
  /*
   * The rank as the path arrives at it, not as the number alone implies.
   *
   * Same derivation the halo uses, so the ring and the words can never
   * disagree about what somebody is wearing — which they would the moment
   * anybody was held on grace.
   */
  const standing = rankThrough(pilotEloSeries(state.matches, me.id, me.elo));
  const rank = rankFor(me.elo, standing.index);

  return (
    <>
      <button className="you-chip" onClick={() => setOpen(true)} aria-label="your profile">
        <RankedAvatar pilot={me} size={26} />
        {ranked && (
          <span className="you-chip-rank" style={{ color: rank.color }}>
            {rank.name}
            <span className="you-chip-level">{'I'.repeat(rank.level)}</span>
          </span>
        )}
        <span className="you-chip-lp num">
          {ranked ? display(me.elo) : `${me.placementGames}/${PLACEMENT_GAMES}`}
        </span>
      </button>
      {open && <MemberProfile pilot={me} onClose={() => setOpen(false)} />}
    </>
  );
}

function Shell() {
  const route = useRoute();
  const { state, online, error, loading } = useStore();

  /**
   * The screen the drawer is covering.
   *
   * A deck is a drawer, not a destination, so opening one must not change what
   * is behind it. Forcing the pool underneath unmounted whatever you were
   * actually on — and remounting Standings threw away which ladder you were
   * reading, so closing a deck you opened from the deck ladder dropped you back
   * on guildmates.
   */
  const behind = useRef<Exclude<Route, { name: 'deck' }>>({ name: 'decks' });
  if (route.name !== 'deck') behind.current = route;
  const page = route.name === 'deck' ? behind.current : route;

  // Fill in any art that is missing once there is a connection to fill it from.
  useEffect(() => {
    if (online && state.decks.length > 0) void warmArtCache(state.decks);
  }, [online, state.decks]);

  const title =
    page.name === 'log'
      ? 'Play'
      : page.name === 'decks'
        ? 'Decks'
        : page.name === 'guildmates'
          ? // Named, because one day there will be more than one of them.
            `${state.settings.guildName} Guild`
          : 'Standings';

  return (
    <div className="app">
      {/* Standalone on a home screen has no address bar and no pull-to-refresh
          of its own, so the gesture everybody tries first has to be built. */}
      <PullToRefresh />

      <header className="topbar">
        <h1>{title}</h1>
        {/*
          Just you.

          The sync dot lived here and has gone to the Guild tab, beside signing
          out — which is the one place its answer changes what somebody does.
          Green for "nothing is wrong" is a light that is on all evening, and it
          was holding width that your own rank uses better.
        */}
        <YouChip />
      </header>

      <main>
        {error && <div className="banner">{error}</div>}
        {loading ? (
          <div className="empty">
            <span className="spinner" />
          </div>
        ) : page.name === 'log' ? (
          <Play />
        ) : page.name === 'decks' ? (
          <Decks />
        ) : page.name === 'guildmates' ? (
          <Guild />
        ) : (
          <Standings />
        )}
      </main>

      {/* Driven by the route so the back gesture closes it, which is how people
          expect to get out of something that slid up from the bottom. */}
      {route.name === 'deck' && <DeckDetail id={route.id} onClose={() => history.back()} />}

      <nav className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.route}
            aria-current={
              route.name === tab.route || (route.name === 'deck' && tab.route === 'decks')
                ? 'page'
                : undefined
            }
            onClick={() => navigate({ name: tab.route } as Route)}
          >
            <span className="tab-icon">
              <tab.Icon />
            </span>
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
